<template>
  <core-card class="tablecontent-root">
    <SectionTitle
      :title="title"
      :title-detail="unitText(chartUnitPosition.TITLE)"
      :subtitle="subtitle"
    />
    <v-data-table
      :items="items"
      :class="{ 'full-height': fullHeight }"
      :mobile-breakpoint="0"
      hide-default-header
      v-bind="$attrs"
    >
      <template #header>
        <thead>
          <tr>
            <th
              v-for="header in columns.map((c) => c.header)"
              :key="header.text"
              :class="alignmentClass(header)"
              :style="columnStyles(header)"
            >
              <span
                :class="extraHeaderClass(header)"
                v-if="header.extra"
                >{{ header.extra }}
              </span>
              <text-tooltip v-if="!header.hideHeader">
                <span class="text-label color-text-secondary">{{
                  header.text
                }}</span>
              </text-tooltip>
            </th>
            <th
              v-if="hasAlert"
              style="width: 30px"
            ></th>
            <!-- Alert Column -->
          </tr>
        </thead>
      </template>
      <template #item="{ item }">
        <tr>
          <td
            v-for="column in columns"
            :key="column.header.text"
            :class="alignmentClass(column.header)"
            :style="columnStyles(column.header)"
          >
            <template v-if="item[column.cell.key]">
              <!-- component:is allows to pass the name of the component and it's properties -->
              <component
                :is="column.cell.component"
                v-if="column.cell.component"
                v-bind="column.cell.props(item[column.cell.key])"
              />
              <text-tooltip v-else>
                <span class="text-body-1">{{
                  item[column.cell.key].toFixed?.(column.cell.decimals) ??
                  item[column.cell.key]
                }}</span>
              </text-tooltip>
            </template>
          </td>
          <td
            v-if="hasAlert"
            style="width: 30px"
          >
            <!-- Alert Column -->
            <TrendChip
              :risk="
                (() => {
                  switch (item.status) {
                    case chartStatus.WARNING:
                      return 2
                    case chartStatus.ERROR:
                      return 3
                    default:
                      return undefined
                  }
                })()
              "
            />
          </td>
        </tr>
      </template>

      <!-- SummaryRow -->
      <template
        v-if="summaryRow"
        #body.append
      >
        <tr class="summary-row">
          <td
            v-for="(column, index) in columns"
            :key="column.header.text"
            :class="column.header.align === 'end' ? 'text-end' : 'text-start'"
          >
            <template v-if="index === 0">
              <!-- Show the label in the first cell -->
              {{
                $te(`views.minds.summaryType.${summaryRow}`)
                  ? $t(`views.minds.summaryType.${summaryRow}`)
                  : ''
              }}
              <!-- Return the summary label if translation key exists -->
            </template>
            <template v-else>
              {{
                column.cell.displayInSummaryRow
                  ? summary(summaryRow, column.cell.key, column.cell.decimals)
                  : ''
              }}
            </template>
          </td>
        </tr>
      </template>
    </v-data-table>
  </core-card>
</template>

<script>
import { chartStatus, chartSummaryType, chartUnitPosition } from '@/utils/enum'
import SectionTitle from '@/components/core/SectionTitle.vue'
import TextTooltip from '@/components/core/TextTooltip.vue'
import TrendChip from '@/components/DashViews/Charts/TrendChip.vue'

export default {
  name: 'TableContent',
  components: {
    SectionTitle,
    TextTooltip,
    TrendChip,
  },
  inheritAttrs: false, //allows to pass all unassigned attributes to the v-data-table
  props: {
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      required: false,
      default: '',
    },
    columns: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    summaryRow: {
      type: String,
      default: undefined,
    },
    unit: {
      type: String,
      required: false,
      default: '',
    },
    unitPosition: {
      type: String,
      required: false,
      default: '',
    },
    fullHeight: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      chartStatus,
      chartUnitPosition,
      breakpoints: ['xs', 'sm', 'md', 'lg', 'xl'],
    }
  },
  computed: {
    hasAlert() {
      return this.items.some(
        (item) =>
          item.status === chartStatus.WARNING ||
          item.status === chartStatus.ERROR,
      )
    },
  },
  methods: {
    summary(type, key, decimals) {
      if (this.items.length === 0) return ''

      switch (type) {
        case chartSummaryType.TOTAL:
          return this.items
            .reduce((sum, item) => sum + (item[key] || 0), 0)
            .toFixed(decimals)
        case chartSummaryType.AVERAGE:
          return (
            this.items.reduce((sum, item) => sum + (item[key] || 0), 0) /
            this.items.length
          ).toFixed(decimals)
        default:
          return ''
      }
    },
    columnStyles(header) {
      const widths = header.width || {}
      const active = this.$vuetify.breakpoint.name
      const value = this.breakpoints
        .slice(0, this.breakpoints.indexOf(active) + 1) //get the active breakpoint and smaller
        .reverse() //start with the bigger breakpoint (active)
        .find((key) => widths[key]) //breaks when it finds a value in widths

      if (!widths[value]) return {}

      return {
        //forces ellipsis only when the width if forced
        ...(widths[value] && { width: widths[value] }),
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      }
    },
    alignmentClass(header) {
      return [header.align === 'end' ? 'text-end' : 'text-start']
    },
    extraHeaderClass(header) {
      const baseClass = ['text-label']
      const baseColorClass = header.extraColor
        ? header.extraColor
        : 'color-text-secondary'
      return [...baseClass, baseColorClass, ...(header.extraClass || [])]
    },
    unitText(position) {
      return this.unitPosition == position ? this.unit : ''
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/styles/material-dashboard/variables.scss';

::v-deep(.v-data-table) {
  overflow: auto;
  &.full-height {
    &,
    .v-data-table__wrapper,
    table {
      height: 100%;
    }
  }

  &.v-data-table--fixed-header thead th {
    box-shadow: inset 0 -1px 0 $divider-light;
  }

  & > .v-data-table__wrapper > table {
    table-layout: fixed;
    width: 100%;

    tbody > tr:not(:last-child) {
      & > td:not(.v-data-table__mobile-row),
      & > th:not(.v-data-table__mobile-row) {
        border-color: $divider-light;
      }
    }

    tbody
      > tr:hover:not(.v-data-table__expanded__content):not(
        .v-data-table__empty-wrapper
      ) {
      background: transparent;
    }

    thead > tr > th {
      height: auto;
    }

    th {
      padding: 7px 0;
      width: auto;

      & + th {
        padding-left: 8px;
      }
    }

    td {
      padding: 0;
      width: auto;

      & + td {
        padding-left: 8px;
      }
    }
  }

  //Header fix firefox
  thead tr {
    height: 35px;
  }
  thead th {
    height: 35px;
    white-space: nowrap;
  }
  tbody {
    height: calc(100%+35px);
  }
}
//mobile
::v-deep(.v-data-table--mobile > .v-data-table__wrapper tbody) {
  width: 100%;

  tr {
    width: 100%;
    display: flex;

    td {
      width: 100%;
    }
  }
}

.tablecontent-root {
  &.v-card.v-card-dense {
    ::v-deep(.v-card__text) {
      padding: 16px 16px 8px 16px;
      gap: 8px;
    }
  }
}

.summary-row {
  color: $text-secondary;
  max-height: 32px;

  td {
    border-top: thin solid $divider-main;
    font-family: $body-font-family;
  }
}
</style>

<!-- DOCUMENTATION --

TableContent.vue

  Description:
  This component renders a card with a data table.
  Each column is defined via the `columns` prop.

  Props:
  - title: String                                       The main title of the card
  - subtitle: String        (Optional)                  Subtitle used for units
  - columns: Array                                      Array of column config objects, including headers, keys, and optional components
  - items: Array                                        Data items to render
  - summary-row: String     (Optional)                  Defines which calculation to apply in the summary row (e.g., "total", "average")
  - unit: String                                        The unit of the data
  - unit-position: String   (Optional)                  The position of the unit (chartUnitPosition)
  - full-height: Boolean    (Optional default true)     Makes the table stretches to fill the parent height

  ----------------------

  Column Structure:
  Each object in `columns` can contain:
  - header: {
      text: String                                                  The label to show in the table header
      extra: String                                                 Additional information to display over the header
      extraColor: String                                            Color class to be used for the extra header
      extraClass: Array                                             Additional classes to be applied to the extra header
      sortable: Boolean               (Optional - default true)     Whether the column is sortable
      align: String                   (Optional)                    'start' or 'end' to align text
      hideHeader: Boolean             (Optional - default false)    Hides the column header, but still displays its data cells
      width: array of string          (Optional)                    Defines widths per breakpoint (e.g., xs, sm, md) to control responsive column sizing
    }

  - cell: {
      key: String                                                   The key to use from the `item` object
      displayInSummaryRow?: Boolean   (Optional - default false)    Display a computed value (like total or average) in the summary row at the bottom
      decimals?: Number               (Optional)                    Number of decimals to display in this column
      component?: String              (Optional)                    Component name (needs to be imported in this file, like TrendChip)
      props?: Function                (paired with component)       Function that receives the item[key] and returns an object of props for the component
    }

  Example:
  columns: [
    {
      header: {
        text: this.$t('views.minds.production.columnsLabel')[0], align: 'start', sortable: false,
        width: {
          xs: '74px',
          md: 'auto',
        }
      },
      cell: { key: 'settings' }
    },
    {
      header: { text: this.$t('views.minds.production.columnsLabel')[1], sortable: false },
      cell: { key: 'theoretical', displayInSummaryRow: true, decimals: 1 }
    },
    {
      header: { hideHeader: true },
      cell: {
        key: 'status',
        component: 'TrendChip',
        props: (status) => ({
          risk: status.value,
        })
      }
    }
  ]

-->